import { TemplateComponentProps } from 'experience-template-renderer-react';
import * as React from 'react';

import { SortAndFiltersViewType } from '@shared-ui/retail-sort-and-filter-lodging';
import { useClickstreamAnalytics } from '@shared-ui/clickstream-analytics-context';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { useViewTrackerContext } from 'src/common/stores/contexts/viewTracker';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkScrollable } from 'uitk-react-scrollable';
import { UitkSpacing } from 'uitk-react-spacing';

import { MapPreview } from './map/map-preview';
import { UniversalSortFilterWrapper } from './sort-filter/universal-sort-filter';
import { z } from 'zod';
import { useExperiment } from '@shared-ui/experiment-context';
import { useBexApi } from '@shared-ui/bex-api-context';

interface FilterbarProps {
  templateComponent: TemplateComponentProps['templateComponent'] & {
    config: {
      view: SortAndFiltersViewType;
      mapSortAndFilterViewType?: SortAndFiltersViewType;
    };
  };
}
export const Filterbar: React.FunctionComponent<FilterbarProps> = ({ templateComponent }) => {
  const { config } = templateComponent;

  const configSchema = z.optional(
    z.object({
      view: z.optional(z.nativeEnum(SortAndFiltersViewType)),
      mapSortAndFilterViewType: z.optional(z.nativeEnum(SortAndFiltersViewType)),
    })
  );
  const result = configSchema.safeParse(config);

  if (!result.success) {
    return null;
  }
  const { view, mapSortAndFilterViewType } = result.data ?? {};

  return (
    <Viewport>
      <ViewSmall>
        <Buttons withMapButton={true} dialogView={view} mapSortAndFilterViewType={mapSortAndFilterViewType} />
      </ViewSmall>
      <ViewLarge>
        <Buttons withMapButton={false} dialogView={view} mapSortAndFilterViewType={mapSortAndFilterViewType} />
      </ViewLarge>
    </Viewport>
  );
};

Filterbar.displayName = 'Filterbar';

interface ButtonsProps {
  withMapButton?: boolean;
  dialogView?: SortAndFiltersViewType;
  mapSortAndFilterViewType?: SortAndFiltersViewType;
}

const Buttons = ({ withMapButton, dialogView, mapSortAndFilterViewType }: ButtonsProps) => {
  const viewTracker = useViewTrackerContext();
  const tracker = useClickstreamAnalytics();

  const { context } = useBexApi();
  const isMobileWeb = context.device.type === ('MOBILE_PHONE' || 'MOBILE_TABLET');
  const quickFiltersExperimentName = `Introduce_Quick_Sort_and_Filters_at_top_of_SRP_${isMobileWeb ? 'mweb' : 'web'}`;
  const { exposure } = useExperiment(quickFiltersExperimentName);
  const isControl = exposure.bucket === 0;

  const mapTrigger = withMapButton && (
    <div data-testid="map-trigger">
      <MapPreview viewTracker={viewTracker} tracker={tracker} dialogView={dialogView} mapSortAndFilterViewType={mapSortAndFilterViewType} />
    </div>
  );

  return (
    <>
      <UitkLayoutFlex>
        <UitkLayoutFlexItem>
          <UitkScrollable direction="horizontal">
            <UitkSpacing
              padding={
                isControl
                  ? {
                      small: { inline: 'three', block: 'four' },
                      medium: { inline: 'six', block: 'four' },
                      large: { inline: 'six', block: 'four' },
                    }
                  : {
                      small: { inline: 'two' },
                      block: 'one',
                    }
              }
            >
              <UniversalSortFilterWrapper view={SortAndFiltersViewType.FILTER_BAR} />
            </UitkSpacing>
          </UitkScrollable>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
      {mapTrigger}
    </>
  );
};
